/**
 * Панель ставки.
 *
 * Одна кнопка, которая меняет смысл по фазе раунда: поставить, вывести или
 * ждать. Так игрок не ищет нужный элемент в спешке — а в краше счёт идёт на
 * десятые доли секунды.
 *
 * Автоучастие ставит ту же сумму и тот же автовывод, что заданы выше. Если
 * лапок не хватает — сессия встаёт на паузу, а не молча пропускает раунды.
 */

import { useEffect, useState } from "react";

import {
  CRASH_ERROR_CODES,
  CrashPhase,
  formatMultiplier,
  formatPaws,
  hasPremiumPerks,
  maxCrashAutoRounds,
  multiplierAt,
  parsePaws,
} from "@koshkodrop/shared";

import { haptic } from "../../lib/telegram";
import { useCrash } from "../../store/crash";
import { useSession } from "../../store/session";

const QUICK_STAKES = ["50", "100", "200", "500"];
const AUTO_PRESETS = [3, 5, 10] as const;
const PAWS_SCALE = 1000n;
/** Множитель автовывода живёт в браузере: это предпочтение игрока, а не ставка. */
const AUTO_CASHOUT_KEY = "koshkodrop.crash.autoCashout";
const AUTO_JOIN_KEY = "koshkodrop.crash.autoJoin";

/** Раунд, в который уже отправили автоставку — чтобы эффект не слал её дважды. */
let lastAutoBetRoundId: string | null = null;
/** Раунд, ставку в котором ещё не засчитали в оставшиеся. */
let pendingAutoRoundId: string | null = null;
let countedAutoRoundId: string | null = null;
/** Ошибка, которая уже висела на экране в момент отправки — её нельзя считать отказом автоставки. */
let lastErrorAtAutoSend: { code: string; message: string } | null = null;

interface AutoJoin {
  total: number;
  left: number;
}

export function BetPanel() {
  const { round, myBet, placeBet, cashout, connection, lastError } = useCrash();
  const balance = useSession((state) => state.balance);
  const isReady = useSession((state) => state.status === "ready");
  const group = useSession((state) => state.user?.group ?? "user");
  const premium = hasPremiumPerks(group);
  const maxAuto = maxCrashAutoRounds(group);

  const [stakeText, setStakeText] = useState("50");
  const [autoCashout, setAutoCashout] = useState(readSavedAuto);
  const [savedAuto, setSavedAuto] = useState(readSavedAuto);
  const [liveMultiplier, setLiveMultiplier] = useState(100);
  const [auto, setAuto] = useState<AutoJoin>(readAutoJoin);
  const [autoPause, setAutoPause] = useState<string | null>(null);

  const phase = round?.phase ?? CrashPhase.Betting;
  const hasActiveBet = myBet?.status === "active";
  const cashoutAt = resolveAutoCashout(autoCashout, savedAuto);
  const autoOn = premium && auto.left > 0;

  useEffect(() => {
    if (phase !== CrashPhase.Running || !round?.startedAt) return;

    const tick = () => {
      const now = useCrash.getState().serverNow();
      setLiveMultiplier(multiplierAt(now - (round.startedAt ?? now)));
    };

    tick();
    const timer = setInterval(tick, 100);
    return () => clearInterval(timer);
  }, [phase, round?.startedAt]);

  const stake = safeParse(stakeText);
  const notEnough = stake !== null && stake > balance;
  const canBet =
    isReady && connection === "open" && phase === CrashPhase.Betting && !myBet && !notEnough;
  const roundId = round?.id ?? null;

  const commitAuto = (updater: (current: AutoJoin) => AutoJoin) => {
    setAuto((current) => {
      const next = updater(current);
      writeAutoJoin(next);
      return next;
    });
  };

  const pauseAuto = (reason: string) => {
    setAutoPause(reason);
    haptic.error();
  };

  useEffect(() => {
    if (!premium || auto.left <= 0 || autoPause) return;
    if (!isReady || connection !== "open") return;
    if (!roundId || phase !== CrashPhase.Betting || myBet) return;
    if (lastAutoBetRoundId === roundId) return;

    if (!stake || stake <= 0n) {
      pauseAuto("Задайте ставку");
      return;
    }
    if (stake > balance) {
      pauseAuto("Не хватает лапок");
      return;
    }

    lastErrorAtAutoSend = useCrash.getState().lastError;
    lastAutoBetRoundId = roundId;
    pendingAutoRoundId = roundId;
    placeBet(stake, cashoutAt);
  }, [
    premium,
    auto.left,
    autoPause,
    isReady,
    connection,
    roundId,
    phase,
    myBet,
    stake,
    balance,
    cashoutAt,
    placeBet,
  ]);

  useEffect(() => {
    if (!autoOn || !roundId || !myBet) return;
    if (pendingAutoRoundId !== roundId) return;
    if (countedAutoRoundId === roundId) return;

    countedAutoRoundId = roundId;
    pendingAutoRoundId = null;
    commitAuto((current) => {
      const left = Math.max(0, current.left - 1);
      return left <= 0 ? { total: 0, left: 0 } : { ...current, left };
    });
  }, [autoOn, roundId, myBet]);

  useEffect(() => {
    if (!autoOn || !lastError || !roundId) return;
    if (pendingAutoRoundId !== roundId) return;
    if (lastError === lastErrorAtAutoSend) return;

    pendingAutoRoundId = null;
    if (lastError.code === CRASH_ERROR_CODES.InsufficientFunds) {
      pauseAuto("Не хватает лапок");
      return;
    }
    if (
      lastError.code === CRASH_ERROR_CODES.StakeTooLow ||
      lastError.code === CRASH_ERROR_CODES.StakeTooHigh
    ) {
      pauseAuto(lastError.message);
      return;
    }
    if (lastError.code === CRASH_ERROR_CODES.BettingClosed) {
      lastAutoBetRoundId = null;
    }
  }, [autoOn, lastError, roundId]);

  const pinned = autoCashout.trim() === savedAuto.trim();

  const handleBet = () => {
    if (!stake || stake <= 0n) {
      haptic.error();
      return;
    }
    placeBet(stake, cashoutAt);
  };

  const pinAutoCashout = () => {
    const trimmed = autoCashout.trim();
    if (trimmed === "") {
      writeSavedAuto("");
      setAutoCashout("");
      setSavedAuto("");
      haptic.select();
      return;
    }
    if (parseAutoCashout(trimmed) === null) {
      haptic.error();
      return;
    }
    writeSavedAuto(trimmed);
    setSavedAuto(trimmed);
    haptic.success();
  };

  const startAuto = (rounds: number) => {
    if (rounds > maxAuto) {
      haptic.error();
      return;
    }
    if (autoOn && auto.total === rounds && !autoPause) {
      stopAuto();
      return;
    }
    haptic.select();
    setAutoPause(null);
    lastAutoBetRoundId = myBet && round ? round.id : lastAutoBetRoundId;
    commitAuto(() => ({ total: rounds, left: rounds }));
  };

  const stopAuto = () => {
    haptic.select();
    setAutoPause(null);
    pendingAutoRoundId = null;
    commitAuto(() => ({ total: 0, left: 0 }));
  };

  const resumeAuto = () => {
    if (!stake || stake <= 0n) {
      pauseAuto("Задайте ставку");
      return;
    }
    if (stake > balance) {
      pauseAuto("Не хватает лапок");
      return;
    }
    haptic.select();
    setAutoPause(null);
  };

  return (
    <div className="space-y-3 rounded-3xl border border-white/5 bg-night-850/80 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-white/40">Ставка</span>
        <button
          type="button"
          onClick={() => {
            haptic.select();
            setStakeText(formatPlain(balance));
          }}
          className="text-xs font-semibold text-paws-400 active:opacity-60"
        >
          Всё: {formatPaws(balance)} 🐾
        </button>
      </div>

      <div className="flex items-stretch gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-2xl bg-night-900 px-3 py-2.5">
          <input
            value={stakeText}
            onChange={(event) => setStakeText(event.target.value.replace(/[^\d.,]/g, ""))}
            inputMode="decimal"
            placeholder="0"
            className="tabular w-full bg-transparent text-lg font-bold text-white outline-none placeholder:text-white/25"
          />
          <span className="text-sm font-semibold text-paws-400">🐾</span>
        </div>
        <button
          type="button"
          onClick={() => {
            haptic.tap();
            setStakeText(halve(stakeText));
          }}
          className="rounded-2xl bg-night-900 px-3 text-sm font-bold text-white/60 active:bg-night-800"
        >
          ½
        </button>
        <button
          type="button"
          onClick={() => {
            haptic.tap();
            setStakeText(double(stakeText));
          }}
          className="rounded-2xl bg-night-900 px-3 text-sm font-bold text-white/60 active:bg-night-800"
        >
          2×
        </button>
      </div>

      <div className="flex gap-2">
        {QUICK_STAKES.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              haptic.select();
              setStakeText(value);
            }}
            className={`flex-1 rounded-xl py-2 text-sm font-bold transition-colors ${
              stakeText === value
                ? "bg-paws-500 text-night-950"
                : "bg-night-900 text-white/55 active:bg-night-800"
            }`}
          >
            {value}
          </button>
        ))}
      </div>

      <div className="flex items-stretch gap-2">
        <div
          className={`flex flex-1 items-center gap-2 rounded-2xl px-3 py-2.5 ${
            autoOn && cashoutAt !== null ? "bg-paws-500/10 ring-1 ring-paws-500/30" : "bg-night-900"
          }`}
        >
          <span className="text-xs font-semibold text-white/40">Автовывод</span>
          <input
            value={autoCashout}
            onChange={(event) => setAutoCashout(event.target.value.replace(/[^\d.,]/g, ""))}
            inputMode="decimal"
            placeholder="выкл"
            className="tabular w-full bg-transparent text-right text-base font-bold text-white outline-none placeholder:text-white/25"
          />
          <span className="text-sm font-semibold text-white/40">x</span>
        </div>
        <button
          type="button"
          onClick={pinAutoCashout}
          className={`rounded-2xl px-3.5 text-sm font-bold active:scale-[0.97] ${
            pinned && savedAuto.trim() !== ""
              ? "bg-paws-500 text-night-950"
              : "bg-night-900 text-white/60 active:bg-night-800"
          }`}
        >
          {pinned && savedAuto.trim() !== "" ? "Вкл" : "Ок"}
        </button>
      </div>

      <AutoJoinCard
        premium={premium}
        maxAuto={maxAuto}
        auto={auto}
        paused={autoPause}
        cashoutAt={cashoutAt}
        stake={stake}
        phase={phase}
        inRound={Boolean(myBet)}
        onStart={startAuto}
        onStop={stopAuto}
        onResume={resumeAuto}
      />

      {hasActiveBet && phase === CrashPhase.Running ? (
        <button
          type="button"
          onClick={cashout}
          className="animate-pulse-ring w-full rounded-2xl bg-win-500 py-4 text-lg font-extrabold text-night-950 active:scale-[0.98]"
        >
          Забрать {formatPaws(payoutPreview(myBet.stake, liveMultiplier))} 🐾
        </button>
      ) : hasActiveBet ? (
        <button
          type="button"
          disabled
          className="w-full rounded-2xl bg-night-700 py-4 text-base font-bold text-white/60"
        >
          {autoOn ? "Авто · ставка принята" : "Ставка принята, ждём запуска"}
        </button>
      ) : myBet?.status === "cashed_out" ? (
        <button
          type="button"
          disabled
          className="w-full rounded-2xl bg-win-500/20 py-4 text-base font-bold text-win-400"
        >
          Забрано {formatPaws(BigInt(myBet.payout ?? "0"))} 🐾
        </button>
      ) : myBet?.status === "lost" && phase === CrashPhase.Crashed ? (
        <button
          type="button"
          disabled
          className="w-full rounded-2xl bg-lose-500/15 py-4 text-base font-bold text-lose-400"
        >
          Слил {formatPaws(BigInt(myBet.stake))} 🐾
        </button>
      ) : (
        <button
          type="button"
          onClick={handleBet}
          disabled={!canBet}
          className="w-full rounded-2xl bg-paws-500 py-4 text-lg font-extrabold text-night-950 transition-opacity active:scale-[0.98] disabled:opacity-35"
        >
          {notEnough
            ? "Не хватает лапок"
            : autoOn && autoPause
              ? "Авто на паузе"
              : autoOn
                ? phase === CrashPhase.Betting
                  ? "Авто · ставлю"
                  : "Авто · ждём раунд"
                : phase === CrashPhase.Betting
                  ? "Поставить"
                  : "Ждём следующий раунд"}
        </button>
      )}
    </div>
  );
}

function AutoJoinCard({
  premium,
  maxAuto,
  auto,
  paused,
  cashoutAt,
  stake,
  phase,
  inRound,
  onStart,
  onStop,
  onResume,
}: {
  premium: boolean;
  maxAuto: number;
  auto: AutoJoin;
  paused: string | null;
  cashoutAt: number | null;
  stake: bigint | null;
  phase: CrashPhase;
  inRound: boolean;
  onStart: (rounds: number) => void;
  onStop: () => void;
  onResume: () => void;
}) {
  const running = premium && auto.left > 0;
  const done = running ? Math.max(0, auto.total - auto.left) : 0;
  const progress = auto.total > 0 ? Math.min(100, (done / auto.total) * 100) : 0;
  const status = paused
    ? paused
    : !running
      ? null
      : inRound && phase === CrashPhase.Running
        ? cashoutAt !== null
          ? `в игре · вывод ${formatMultiplier(cashoutAt)}`
          : "в игре · заберите сами"
        : inRound
          ? "ставка принята"
          : phase === CrashPhase.Betting
            ? "ставлю в этот раунд"
            : "ждём следующий раунд";

  return (
    <div
      className={`space-y-2.5 rounded-2xl border p-3 ${
        paused
          ? "border-lose-500/30 bg-lose-500/10"
          : running
            ? "border-paws-500/35 bg-paws-500/10"
            : "border-white/8 bg-night-900/80"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className={`size-1.5 rounded-full ${
              paused ? "bg-lose-400" : running ? "animate-pulse bg-paws-400" : "bg-white/25"
            }`}
          />
          <p className="text-[10px] font-bold uppercase tracking-wide text-white/45">
            {premium ? "Автоучастие" : "Автоучастие · Premium"}
          </p>
        </div>
        {running && (
          <p className="tabular text-[11px] font-extrabold text-paws-300">
            {inRound && done > 0
              ? `раунд ${done} из ${auto.total}`
              : `ещё ${auto.left} из ${auto.total}`}
          </p>
        )}
      </div>

      {running && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Chip>
            {stake && stake > 0n ? `${formatPaws(stake)} 🐾` : "ставка?"}
          </Chip>
          <Chip>
            {cashoutAt !== null ? `вывод ${formatMultiplier(cashoutAt)}` : "без автовывода"}
          </Chip>
        </div>
      )}

      {running && (
        <div className="h-1.5 overflow-hidden rounded-full bg-night-950/70">
          <div
            className={`h-full rounded-full transition-[width] ${paused ? "bg-lose-400" : "bg-paws-400"}`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {status && (
        <p className={`text-[11px] font-bold ${paused ? "text-lose-300" : "text-white/50"}`}>
          {status}
        </p>
      )}

      <div className="flex gap-2">
        {AUTO_PRESETS.map((value) => {
          const locked = !premium || value > maxAuto;
          const active = running && auto.total === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => onStart(value)}
              className={`flex-1 rounded-xl py-2 text-xs font-extrabold ${
                active && !paused
                  ? "bg-paws-500 text-night-950"
                  : active && paused
                    ? "bg-lose-500/30 text-lose-400"
                    : locked
                      ? "bg-night-850 text-white/25"
                      : "bg-night-850 text-white/60 active:bg-night-800"
              }`}
            >
              {value}
            </button>
          );
        })}
        {running ? (
          <button
            type="button"
            onClick={paused ? onResume : onStop}
            className={`rounded-xl px-3 text-xs font-extrabold ${
              paused ? "bg-paws-500 text-night-950" : "bg-night-850 text-white/70"
            }`}
          >
            {paused ? "Далее" : "Стоп"}
          </button>
        ) : (
          <button
            type="button"
            disabled
            className="rounded-xl bg-night-850 px-3 text-xs font-extrabold text-white/25"
          >
            выкл
          </button>
        )}
      </div>
    </div>
  );
}

function Chip({ children }: { children: string }) {
  return (
    <span className="rounded-lg bg-night-950/55 px-2 py-0.5 text-[10px] font-bold text-white/70">
      {children}
    </span>
  );
}

function safeParse(text: string): bigint | null {
  const normalized = text.replace(",", ".").trim();
  if (!normalized) return null;
  try {
    return parsePaws(normalized);
  } catch {
    return null;
  }
}

function parseAutoCashout(text: string): number | null {
  const normalized = text.trim().replace(",", ".");
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 1) return null;
  return Math.round(value * 100);
}

/** Живое поле, а если оно пустое или битое — закреплённый автовывод. */
function resolveAutoCashout(draft: string, saved: string): number | null {
  return parseAutoCashout(draft) ?? parseAutoCashout(saved);
}

function readSavedAuto(): string {
  try {
    return localStorage.getItem(AUTO_CASHOUT_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeSavedAuto(text: string): void {
  try {
    if (text.trim() === "") localStorage.removeItem(AUTO_CASHOUT_KEY);
    else localStorage.setItem(AUTO_CASHOUT_KEY, text);
  } catch {
    // Приватный режим или переполненное хранилище — поле просто не запомнится
  }
}

function readAutoJoin(): AutoJoin {
  try {
    const raw = localStorage.getItem(AUTO_JOIN_KEY);
    if (!raw) return { total: 0, left: 0 };
    if (/^\d+$/.test(raw)) {
      const left = clampAuto(Number(raw));
      return left > 0 ? { total: left, left } : { total: 0, left: 0 };
    }
    const parsed = JSON.parse(raw) as { total?: unknown; left?: unknown };
    const left = clampAuto(Number(parsed.left));
    const total = clampAuto(Number(parsed.total));
    if (left <= 0) return { total: 0, left: 0 };
    return { total: Math.max(total, left), left };
  } catch {
    return { total: 0, left: 0 };
  }
}

function writeAutoJoin(value: AutoJoin): void {
  try {
    if (value.left <= 0) localStorage.removeItem(AUTO_JOIN_KEY);
    else localStorage.setItem(AUTO_JOIN_KEY, JSON.stringify(value));
  } catch {
    // как и автокэшаут: без хранилища просто не запомнится
  }
}

function clampAuto(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.min(10, Math.floor(value));
}

/** Обратное форматирование для поля ввода: без пробелов-разделителей. */
function formatPlain(amount: bigint): string {
  const whole = amount / PAWS_SCALE;
  const fraction = amount % PAWS_SCALE;
  if (fraction === 0n) return whole.toString();
  return `${whole}.${fraction.toString().padStart(3, "0").replace(/0+$/, "")}`;
}

function halve(text: string): string {
  const value = safeParse(text);
  if (!value) return text;
  return formatPlain(value / 2n);
}

function double(text: string): string {
  const value = safeParse(text);
  if (!value) return text;
  return formatPlain(value * 2n);
}

function payoutPreview(stake: string, multiplier: number): bigint {
  return (BigInt(stake) * BigInt(multiplier)) / 100n;
}
